import type { IBetRadarLiveScore, ICricketLiveScore, ILiveScore, IThirdPartyLiveScore } from '@/interface/ILiveScore';
import type { IScore } from '@/interface/IScore';
import { MatchStatus } from '@/interface/enum';
import type { BetRadarLiveScoreTypeFragment, CricketLiveScoreTypeFragment, Maybe, ThirdPartyLiveScoreTypeFragment } from '@/types';

export function isScoreChanged(oldScore: IScore, newScore: IScore) {
    if (oldScore.away === newScore.away && oldScore.home === newScore.home) {
        return false;
    }
    return true;
}

export function getLiveScoreDisplay(score: number): string {
    return score < 0 ? '--' : score.toString();
}

export function formatLiveScore(liveScore: Maybe<{
    betRadar?: Maybe<BetRadarLiveScoreTypeFragment>;
    cricket?: Maybe<CricketLiveScoreTypeFragment>;
    betConstruct?: Maybe<ThirdPartyLiveScoreTypeFragment>;
    betGenius?: Maybe<ThirdPartyLiveScoreTypeFragment>;
    sdtV2?: Maybe<ThirdPartyLiveScoreTypeFragment>;
}> | undefined): ILiveScore {
    if (!liveScore) {
        return {
            betRadar: null,
            cricket: null,
            betConstruct: null,
            betGenius: null,
            sdtV2: null,
        };
    }

    return {
        betRadar: formatBetRadarLiveScore(liveScore.betRadar),
        cricket: formatCricketLiveScore(liveScore.cricket),
        betConstruct: formatThirdPartyLiveScore(liveScore.betConstruct),
        betGenius: formatThirdPartyLiveScore(liveScore.betGenius),
        sdtV2: formatThirdPartyLiveScore(liveScore.sdtV2),
    };
}

function formatBetRadarLiveScore(score: Maybe<BetRadarLiveScoreTypeFragment> | undefined): IBetRadarLiveScore | null {
    if (!score) {
        return null;
    }
    return {
        gameHomeScore: score.gameHomeScore ?? 0,
        gameAwayScore: score.gameAwayScore ?? 0,
        setHomeScore: score.setHomeScore ?? 0,
        setAwayScore: score.setAwayScore ?? 0,
        totalHomeScore: score.totalHomeScore ?? 0,
        totalAwayScore: score.totalAwayScore ?? 0,
        inPlayInning: score.inPlayInning ?? '',
        inPlayPeriod: score.inPlayPeriod ?? '',
        matchStatus: MatchStatus.fromOddsApi(score.matchStatus ?? MatchStatus.Unknown),
        status: score.status ?? 0,
    };
}

const defaultCricketLiveScore = {
    matchId: 0,
    homeScore: 0,
    awayScore: 0,
    homeDismissals: 0,
    awayDismissals: 0,
    innings: 0,
    over: 0,
    delivery: 0,
};

export function formatCricketLiveScore(liveScore: Maybe<CricketLiveScoreTypeFragment> | undefined): ICricketLiveScore {
    if (!liveScore) return defaultCricketLiveScore;
    return {
        matchId: liveScore.matchId,
        homeScore: liveScore.homeScore,
        awayScore: liveScore.awayScore,
        homeDismissals: liveScore.homeDismissals,
        awayDismissals: liveScore.awayDismissals,
        innings: liveScore.innings,
        over: liveScore.over,
        delivery: liveScore.delivery,
    };
}

export function formatThirdPartyLiveScore(score: Maybe<ThirdPartyLiveScoreTypeFragment> | undefined): IThirdPartyLiveScore | null {
    if (!score) {
        return null;
    }
    return {
        gameHomeScore: score.gameHomeScore ?? 0,
        gameAwayScore: score.gameAwayScore ?? 0,
        setHomeScore: score.setHomeScore ?? 0,
        setAwayScore: score.setAwayScore ?? 0,
        totalHomeScore: score.totalHomeScore ?? 0,
        totalAwayScore: score.totalAwayScore ?? 0,
        inPlayPeriod: score.inPlayPeriod ?? '',
        matchStatus: MatchStatus.fromOddsApi(score.matchStatus ?? MatchStatus.Unknown),
        status: score.status ?? 0,
    };
}
